import React, { useState, useEffect } from 'react';
import { Trophy, Flame, Trash2, UserX, UserCheck, Crown, Star, Building2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios, { API } from '@/utils/api';
import { useDialog } from '@/context/DialogContext';

const API_BASE = API.replace('/api', '');

// Google icon as SVG component
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Default tier styles - labels will be fetched dynamically
const DEFAULT_TIER_STYLES = {
  founding_member: { bg: 'bg-gradient-to-r from-yellow-500 to-amber-600', defaultText: 'Founding Member', icon: Crown },
  premium: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', defaultText: 'Premium', icon: Star },
  free: { bg: 'bg-gray-500', defaultText: 'Free', icon: null },
  enterprise: { bg: 'bg-gradient-to-r from-blue-500 to-cyan-500', defaultText: 'Enterprise', icon: Crown }
};

const UserRow = ({ user, index, onDelete, onToggleStatus, onViewDetails, tierLabels = null, onChangeTier = null }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [labels, setLabels] = useState(tierLabels);
  const [showTierDropdown, setShowTierDropdown] = useState(false);
  const [updatingTier, setUpdatingTier] = useState(false);
  const { confirm } = useDialog();

  // Fetch tier labels if not provided
  useEffect(() => {
    if (!tierLabels) {
      axios.get(`${API_BASE}/api/admin/settings/tier-labels`)
        .then(res => setLabels(res.data.labels))
        .catch(() => setLabels(null));
    }
  }, [tierLabels]);

  // Handle tier change
  const handleTierChange = async (newTier) => {
    if (onChangeTier) {
      setUpdatingTier(true);
      try {
        await onChangeTier(user.user_id, newTier);
        setShowTierDropdown(false);
      } finally {
        setUpdatingTier(false);
      }
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete User',
      message: `Are you sure you want to delete ${user.email}? This removes ALL their data permanently.`,
      confirmText: 'Delete User',
      cancelText: 'Cancel',
      variant: 'destructive'
    });
    if (!confirmed) return;
    setIsDeleting(true);
    await onDelete(user.user_id);
    setIsDeleting(false);
  };

  const getMemberDays = () => {
    if (!user.created_at) return null;
    const days = Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1d';
    if (days < 7) return `${days}d`;
    if (days < 30) return `${Math.floor(days / 7)}w`;
    if (days < 365) return `${Math.floor(days / 30)}mo`;
    return `${Math.floor(days / 365)}y`;
  };

  // Format signup date for display
  const getSignupDate = () => {
    if (!user.created_at) return 'N/A';
    try {
      const date = new Date(user.created_at);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const tier = user.membership_tier || 'founding_member';
  const tierStyle = DEFAULT_TIER_STYLES[tier] || DEFAULT_TIER_STYLES.free;
  const TierIcon = tierStyle.icon;
  
  // Get the display label - use custom labels if available, otherwise default
  const tierDisplayLabel = labels?.[tier] || tierStyle.defaultText;

  return (
    <div 
      className={`bg-gradient-to-r from-[#4A0A66]/20 to-[#4A0A66]/10 border border-[#4A0A66]/30 rounded-lg p-3 flex items-center gap-3 hover:border-[#df1283]/50 hover:bg-[#4A0A66]/30 transition-all cursor-pointer ${user.is_revoked ? 'opacity-50' : ''}`}
      onClick={() => onViewDetails(user.user_id)}
      data-testid={`user-row-${user.user_id}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${user.is_revoked ? 'bg-red-500/50' : 'bg-gradient-to-br from-[#df1283] to-[#FF7901]'}`}>
        {(user.display_name || user.name || user.email || 'U')[0].toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-white truncate">{user.display_name || user.name || 'Anonymous'}</p>
          {/* Auth Type Badge - Google vs Email */}
          {user.auth_type === 'google' || user.auth_type === 'google_oauth' ? (
            <span className="bg-blue-500/30 border border-blue-500/50 px-1.5 py-0.5 rounded text-[10px] font-semibold text-blue-300 flex items-center gap-0.5" title="Signed up with Google">
              <GoogleIcon className="w-3 h-3" />
            </span>
          ) : (
            <span className="bg-[#df1283]/20 border border-[#df1283]/50 px-1.5 py-0.5 rounded text-[10px] font-semibold text-[#ff69b4] flex items-center gap-0.5" title="Signed up with Email">
              <Mail className="w-3 h-3" />
            </span>
          )}
          {/* B2B Badge */}
          {user.client_id && (
            <span className="bg-blue-500/80 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white flex items-center gap-0.5" title={`Linked to: ${user.client_name || 'B2B Client'}`}>
              <Building2 className="w-2.5 h-2.5" />
              B2B
            </span>
          )}
          {/* Membership Tier Badge - Clickable with dropdown */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onChangeTier) setShowTierDropdown(!showTierDropdown);
              }}
              className={`${tierStyle.bg} ${tier === 'cancelled' ? '!bg-red-500' : ''} px-1.5 py-0.5 rounded text-[10px] font-semibold text-white flex items-center gap-0.5 ${onChangeTier ? 'cursor-pointer hover:ring-2 hover:ring-white/30' : ''}`}
              title={onChangeTier ? "Click to change tier" : undefined}
            >
              {TierIcon && <TierIcon className="w-2.5 h-2.5" />}
              {tier === 'cancelled' ? 'Cancelled' : tierDisplayLabel}
            </button>
            
            {/* Tier dropdown */}
            {showTierDropdown && onChangeTier && (
              <div 
                className="absolute left-0 top-6 z-50 bg-[#1a0527] border border-[#df1283]/30 rounded-lg shadow-xl p-2 min-w-[130px]"
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-[10px] text-gray-400 px-2 pb-1 border-b border-white/10 mb-1">Change tier:</p>
                {['premium', 'free', 'cancelled'].map(tierOption => (
                  <button
                    key={tierOption}
                    onClick={() => handleTierChange(tierOption)}
                    disabled={updatingTier || tier === tierOption}
                    className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-[#df1283]/20 transition-colors ${
                      tier === tierOption ? 'bg-[#df1283]/10 text-[#df1283]' : 'text-white'
                    } ${updatingTier ? 'opacity-50' : ''}`}
                  >
                    {tierOption === 'premium' ? '⭐ Premium' : 
                     tierOption === 'free' ? '👤 Free' : 
                     tierOption === 'cancelled' ? '🚫 Cancelled' : tierOption}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-[#FDF2F8]/50 truncate">{user.email}</p>
        {user.referral_source && (
          <p className="text-xs text-[#22C55E] truncate mt-0.5">
            via {user.referral_source}
          </p>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs">
        {/* Entries */}
        <div className="text-center">
          <p className="text-sm font-bold text-[#df1283]">{user.entries_count || 0}</p>
          <p className="text-[10px] text-[#FDF2F8]/40">entries</p>
        </div>
        {/* Badges */}
        {user.badges_count > 0 && (
          <div className="text-center">
            <p className="text-sm font-bold text-yellow-400 flex items-center gap-0.5">
              <Trophy className="w-3 h-3" />{user.badges_count}
            </p>
            <p className="text-[10px] text-[#FDF2F8]/40">badges</p>
          </div>
        )}
        {/* Streak */}
        {user.current_streak > 0 && (
          <div className="text-center">
            <p className="text-sm font-bold text-orange-400 flex items-center gap-0.5">
              <Flame className="w-3 h-3" />{user.current_streak}
            </p>
            <p className="text-[10px] text-[#FDF2F8]/40">streak</p>
          </div>
        )}
        {/* Signup Date */}
        <div className="text-center bg-[#4A0A66]/40 rounded px-2 py-1 min-w-[70px]">
          <p className="text-xs font-medium text-[#0FFFE2]">{getSignupDate()}</p>
          <p className="text-[9px] text-[#FDF2F8]/40">{getMemberDays()} ago</p>
        </div>
      </div>
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <Button
          onClick={() => onToggleStatus(user.user_id, !user.is_revoked)}
          variant="ghost"
          size="sm"
          className={`p-1.5 ${user.is_revoked ? 'text-green-400' : 'text-yellow-400'}`}
        >
          {user.is_revoked ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
        </Button>
        <Button
          onClick={handleDelete}
          disabled={isDeleting}
          variant="ghost"
          size="sm"
          className="p-1.5 text-red-400"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default UserRow;
